import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import Icon from '../dashboard/DashboardIcons';
import { normalizeWhatsAppNumber, buildWaMeLink, fillTemplate, VARIABLE_LABELS } from '../../lib/whatsapp';
import { toast } from '../../lib/toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

// نافذة «مراسلة عبر واتساب» — روابط wa.me فقط، بلا أي إرسال آلي. Bottom Sheet بالجوال، Modal بالكمبيوتر.
export const WhatsAppMessageModal = ({
  open, onClose, recipient, variables = {}, relatedType = null, relatedId = null, allowRecipientSearch = false,
}) => {
  const [templates, setTemplates] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');
  const [text, setText] = useState('');
  const [phone, setPhone] = useState(recipient?.phone || '');
  const [stage, setStage] = useState('compose'); // compose | opened
  const [pickedName, setPickedName] = useState(null); // اسم المستلم بعد اختياره من البحث (يتجاوز recipient.name للعرض فقط)
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerQuery, setPickerQuery] = useState('');
  const [pickerPlayers, setPickerPlayers] = useState([]);
  const [pickerLoading, setPickerLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage('compose');
    setPhone(recipient?.phone || '');
    setPickedName(null);
    setPickerOpen(false);
    setPickerQuery('');
    setLoading(true);
    Promise.all([
      axios.get(`${API}/whatsapp/templates`, auth()),
      axios.get(`${API}/club-settings`, auth()).catch(() => ({ data: {} })),
    ]).then(([tRes, sRes]) => {
      setTemplates(tRes.data || []);
      setSettings(sRes.data || {});
      if (tRes.data?.length) setSelectedId(tRes.data[0].id);
    }).catch(() => toast.error('تعذّر تحميل القوالب')).finally(() => setLoading(false));
  }, [open, recipient?.id]);

  const selectedTemplate = useMemo(() => templates.find((t) => t.id === selectedId), [templates, selectedId]);

  useEffect(() => {
    if (!selectedTemplate) return;
    const merged = { club_name: settings?.whatsapp_display_name || variables.club_name, ...variables };
    let filled = fillTemplate(selectedTemplate.body, merged);
    if (settings?.whatsapp_signature) filled += `\n\n${settings.whatsapp_signature}`;
    setText(filled);
  }, [selectedTemplate]);

  const normalizedPhone = useMemo(
    () => normalizeWhatsAppNumber(phone, settings?.whatsapp_country_code || '966'),
    [phone, settings]
  );
  const phoneInvalid = !phone || !normalizedPhone;

  useEffect(() => {
    if (!pickerOpen || pickerPlayers.length || pickerLoading) return;
    setPickerLoading(true);
    axios.get(`${API}/players`, auth())
      .then((res) => setPickerPlayers(Array.isArray(res.data) ? res.data : []))
      .catch(() => toast.error('تعذّر تحميل قائمة اللاعبين'))
      .finally(() => setPickerLoading(false));
  }, [pickerOpen]);

  const pickerResults = useMemo(() => {
    const q = pickerQuery.trim().toLowerCase();
    return pickerPlayers
      .filter((p) => !q || (p.name || '').toLowerCase().includes(q) || (p.guardian_name || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [pickerPlayers, pickerQuery]);

  const selectPickedPlayer = (p) => {
    const resolvedPhone = p.guardian_phone || p.phone || '';
    setPickedName(p.guardian_name ? `${p.guardian_name} (ولي أمر ${p.name})` : p.name);
    setPhone(resolvedPhone);
    setPickerOpen(false);
    setPickerQuery('');
    if (!resolvedPhone) toast.error('لا يوجد رقم مسجَّل لهذا المستلم — أدخله يدوياً');
  };

  if (!open) return null;

  const logAttempt = async (status) => {
    try {
      await axios.post(`${API}/whatsapp/log`, {
        recipient_type: recipient?.type || 'other',
        recipient_name: pickedName || recipient?.name || 'غير معروف',
        recipient_phone_raw: phone,
        recipient_phone_normalized: normalizedPhone,
        template_id: selectedTemplate?.id,
        template_key: selectedTemplate?.key,
        template_name: selectedTemplate?.name,
        final_message: text,
        status,
        related_type: relatedType,
        related_id: relatedId,
      }, auth());
    } catch { /* لا نمنع فتح واتساب لو فشل تسجيل السجل فقط */ }
  };

  const handleOpenWhatsApp = async () => {
    if (phoneInvalid) {
      toast.error('رقم الجوال غير صالح — يرجى تعديله أولاً');
      await logAttempt('failed');
      return;
    }
    window.open(buildWaMeLink(normalizedPhone, text), '_blank', 'noopener,noreferrer');
    await logAttempt('opened');
    setStage('opened');
  };

  const handleCopy = () => {
    navigator.clipboard?.writeText(text);
    toast.success('تم نسخ نص الرسالة');
  };

  return (
    <div className="fixed inset-0 z-[999] flex items-end justify-center bg-black/50 sm:items-center sm:p-4" dir="rtl" onClick={onClose} data-testid="whatsapp-message-modal">
      <div
        className="w-full max-w-lg rounded-t-2xl bg-white p-5 shadow-2xl sm:rounded-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-50 text-emerald-600">
              <Icon name="whatsapp" className="w-5 h-5" />
            </span>
            <h3 className="text-base font-bold text-slate-900">مراسلة عبر واتساب</h3>
          </div>
          <button onClick={onClose} data-testid="whatsapp-modal-close" className="text-slate-400 hover:text-slate-600">
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {loading ? (
          <div className="flex flex-col items-center gap-2 py-10 text-slate-400" data-testid="whatsapp-modal-loading">
            <span className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
            <span className="text-sm">جارٍ التحميل...</span>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="rounded-xl bg-slate-50 p-3">
              <p className="text-xs text-slate-500">المستلم</p>
              <p className="font-bold text-slate-800" data-testid="whatsapp-recipient-name">{pickedName || recipient?.name || '—'}</p>
              <div className="mt-2 flex items-center gap-2">
                <input
                  value={phone}
                  onChange={(e) => { setPhone(e.target.value); setPickedName(null); }}
                  dir="ltr"
                  placeholder="05XXXXXXXX"
                  data-testid="whatsapp-recipient-phone-input"
                  className={`flex-1 rounded-lg border px-3 py-2 text-sm ${phoneInvalid ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                />
                {phoneInvalid && <span className="text-xs font-semibold text-red-500" data-testid="whatsapp-phone-invalid-badge">رقم غير صالح</span>}
              </div>
              {phoneInvalid && !normalizedPhone && (
                <p className="mt-1.5 text-xs text-slate-500">لم يُعثر على رقم تلقائي لهذه الفاتورة — اختر مستلماً من اللاعبين أو أدخل الرقم يدوياً.</p>
              )}

              {allowRecipientSearch && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => setPickerOpen((o) => !o)}
                    data-testid="whatsapp-pick-recipient-toggle"
                    className="flex items-center gap-1 text-xs font-bold text-emerald-700 hover:underline"
                  >
                    <Icon name="users" className="w-3.5 h-3.5" /> {pickerOpen ? 'إغلاق البحث' : 'اختيار مستلم من اللاعبين (بحث بالاسم)'}
                  </button>
                  {pickerOpen && (
                    <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2">
                      <input
                        value={pickerQuery}
                        onChange={(e) => setPickerQuery(e.target.value)}
                        placeholder="ابحث باسم اللاعب أو ولي الأمر..."
                        data-testid="whatsapp-pick-recipient-search"
                        className="mb-2 w-full rounded-lg border border-slate-200 px-2 py-1.5 text-sm"
                      />
                      {pickerLoading ? (
                        <p className="py-2 text-center text-xs text-slate-400">جارٍ التحميل...</p>
                      ) : pickerResults.length === 0 ? (
                        <p className="py-2 text-center text-xs text-slate-400" data-testid="whatsapp-pick-recipient-empty">لا نتائج مطابقة</p>
                      ) : (
                        <div className="max-h-40 space-y-1 overflow-y-auto">
                          {pickerResults.map((p) => {
                            const ph = p.guardian_phone || p.phone;
                            return (
                              <button
                                key={p.id}
                                type="button"
                                onClick={() => selectPickedPlayer(p)}
                                data-testid="whatsapp-pick-recipient-option"
                                className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-right text-xs hover:bg-slate-50"
                              >
                                <span className="font-semibold text-slate-700">{p.guardian_name ? `${p.guardian_name} (${p.name})` : p.name}</span>
                                <span className="text-slate-400" dir="ltr">{ph || 'بلا رقم'}</span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>

            {templates.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4" data-testid="whatsapp-no-templates">لا توجد قوالب متاحة</p>
            ) : (
              <div>
                <label className="mb-1 block text-xs font-semibold text-slate-600">القالب</label>
                <select
                  value={selectedId}
                  onChange={(e) => setSelectedId(e.target.value)}
                  data-testid="whatsapp-template-select"
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                >
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}{t.requires_financial ? ' (مالي)' : ''}</option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="mb-1 block text-xs font-semibold text-slate-600">نص الرسالة (قابل للتعديل)</label>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                rows={6}
                data-testid="whatsapp-message-textarea"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm leading-relaxed"
              />
            </div>

            {stage === 'compose' ? (
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopy}
                  data-testid="whatsapp-copy-button"
                  className="flex items-center gap-1.5 rounded-lg border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-600 hover:bg-slate-50"
                >
                  <Icon name="copy" className="w-4 h-4" /> نسخ
                </button>
                <button
                  onClick={handleOpenWhatsApp}
                  data-testid="whatsapp-open-button"
                  className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-700"
                >
                  <Icon name="whatsapp" className="w-4 h-4" /> فتح في واتساب
                </button>
              </div>
            ) : (
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-center" data-testid="whatsapp-opened-confirmation">
                <p className="text-sm font-semibold text-emerald-700 mb-3">تم فتح واتساب — لم يُرسَل تلقائياً، أكمل الإرسال من هاتفك</p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { logAttempt('confirmed_sent'); onClose(); }}
                    data-testid="whatsapp-confirm-sent-button"
                    className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 text-sm font-bold text-white hover:bg-emerald-700"
                  >
                    تأكيد أنني أرسلتها
                  </button>
                  <button onClick={onClose} data-testid="whatsapp-close-without-confirm" className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-500">
                    إغلاق
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// زر صغير عام لفتح النافذة — يُستخدم داخل الجداول وقوائم الإجراءات
export const WhatsAppButton = ({ recipient, variables, relatedType, relatedId, label = 'واتساب', className = '' }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        data-testid="whatsapp-trigger-button"
        className={className || 'flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50'}
      >
        <Icon name="whatsapp" className="w-4 h-4" /> {label}
      </button>
      <WhatsAppMessageModal
        open={open}
        onClose={() => setOpen(false)}
        recipient={recipient}
        variables={variables}
        relatedType={relatedType}
        relatedId={relatedId}
      />
    </>
  );
};
